<template>
  <div class="fac-page">
    <!-- Header -->
    <div class="fac-header">
      <div>
        <span class="fac-eyebrow">B2B</span>
        <h2 class="fac-title">Facturas</h2>
      </div>
    </div>

    <!-- Listado -->
    <div class="fac-card">
      <div class="fac-card__header">
        <h3 class="fac-card__title">Listado</h3>
        <div class="fac-filters">
          <div class="filter-pills">
            <button
              v-for="f in ['Todas', 'Pendientes', 'Pagadas', 'Abonadas', 'Abonos']"
              :key="f"
              class="pill"
              :class="{ active: filtro === f }"
              @click="filtro = f"
            >{{ f }}</button>
          </div>
          <div class="search-box">
            <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
            <input type="text" class="search-box__input" placeholder="Buscar factura, cliente o pedido..." v-model="buscar" />
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <b-table
          :items="filtradas"
          :fields="columnas"
          class="mb-0"
          hover
          responsive
          show-empty
          :busy="cargando"
          empty-text="No hay facturas"
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #table-busy>
            <div class="text-center text-muted my-2">
              <b-spinner small class="mr-50" /> Cargando…
            </div>
          </template>

          <template #cell(numero)="data">
            <span class="cell-numero-wrap">
              <span class="cell-numero">{{ data.item.numero }}</span>
              <span v-if="data.item.tipo === 'abono'" class="badge-doc badge-doc--abono" :title="`Abona la factura ${data.item.abonaNumero || ''}`">ABONO</span>
            </span>
            <small v-if="data.item.tipo === 'abono' && data.item.abonaNumero" class="text-muted d-block">de {{ data.item.abonaNumero }}</small>
            <small v-else-if="data.item.abonadaPorNumero" class="text-muted d-block">abonada por {{ data.item.abonadaPorNumero }}</small>
          </template>

          <template #cell(pedidoId)="data">
            <span class="cell-mono">{{ data.item.pedidoRef || `#${data.item.pedidoId}` }}</span>
          </template>

          <template #cell(fecha)="data">
            <span class="cell-mono">{{ formatFecha(data.item.fecha) }}</span>
          </template>

          <template #cell(cliente)="data">
            <span
              v-if="data.item.customerId"
              class="cell-cliente cell-cliente--link"
              @click="$router.push({ name: 'customer-profile', params: { id: data.item.customerId } })"
            >{{ nombreCliente(data.item) }}</span>
            <span v-else class="cell-cliente">{{ nombreCliente(data.item) }}</span>
          </template>

          <template #cell(total)="data">
            <span class="cell-mono cell-total" :class="{ 'text-danger': Number(data.item.total) < 0 }">
              {{ data.item.simbolo }}{{ Number(data.item.total).toFixed(2) }}
            </span>
          </template>

          <template #cell(estado)="data">
            <span class="badge-status" :class="`badge-status--${estadoInfo(data.item).clase}`">
              {{ estadoInfo(data.item).texto }}
            </span>
          </template>

          <template #cell(acciones)="data">
            <b-button size="sm" variant="flat-danger" class="btn-icon" title="Ver PDF" :disabled="pdfId === data.item.id" @click="verPdf(data.item)">
              <b-spinner v-if="pdfId === data.item.id" small />
              <i v-else class="fa-solid fa-file-pdf"></i>
            </b-button>
            <b-button
              v-if="data.item.tipo === 'factura' && !data.item.abonadaPor"
              size="sm"
              variant="flat-warning"
              class="btn-icon"
              title="Emitir abono (credit note)"
              @click="abrirAbono(data.item)"
            >
              <i class="fa-solid fa-rotate-left"></i>
            </b-button>
          </template>
        </b-table>
      </div>

      <div class="fac-card__footer">
        <span>{{ filtradas.length }} documentos</span>
        <span>
          Total <b>{{ sumaFiltradas() || '--' }}</b>
        </span>
      </div>
    </div>

    <!-- Motivo del abono -->
    <b-modal
      v-model="abono.visible"
      :title="`Emitir abono — ${(abono.factura && abono.factura.numero) || ''}`"
      centered
      ok-variant="warning"
      :ok-title="abono.emitiendo ? 'Emitiendo…' : 'Emitir abono'"
      cancel-title="Cancelar"
      :ok-disabled="abono.emitiendo"
      @ok.prevent="emitirAbono"
    >
      <p class="text-muted">
        Se emitirá un credit note por el total de la factura
        ({{ abono.factura ? `${abono.factura.simbolo}${Number(abono.factura.total).toFixed(2)}` : '' }})
        con numeración correlativa AB. Si la factura estaba cobrada, la devolución se anota en el historial de pagos del pedido.
      </p>
      <label class="d-block mb-50 font-weight-bold small">Motivo (opcional)</label>
      <b-form-textarea v-model="abono.motivo" rows="2" placeholder="Ej: devolución de la mercancía" />
    </b-modal>
  </div>
</template>

<script>
import {
  BTable, BSpinner, BButton, BModal, BFormTextarea,
} from 'bootstrap-vue'
import axios from '@axios'

export default {
  components: {
    BTable, BSpinner, BButton, BModal, BFormTextarea,
  },
  data() {
    return {
      facturas: [],
      cargando: true,
      buscar: '',
      filtro: 'Todas',
      pdfId: null,
      abono: {
        visible: false, factura: null, motivo: '', emitiendo: false,
      },
      columnas: [
        { key: 'numero', label: 'Nº', sortable: true },
        { key: 'pedidoId', label: 'Pedido' },
        { key: 'fecha', label: 'Fecha', sortable: true },
        { key: 'cliente', label: 'Cliente' },
        { key: 'total', label: 'Total', sortable: true },
        { key: 'estado', label: 'Estado' },
        { key: 'acciones', label: '', class: 'text-nowrap', thStyle: { width: '110px' } },
      ],
    }
  },
  computed: {
    filtradas() {
      let lista = this.facturas
      if (this.filtro === 'Pendientes') lista = lista.filter(f => f.tipo === 'factura' && !f.pagada && !f.abonadaPor)
      if (this.filtro === 'Pagadas') lista = lista.filter(f => f.tipo === 'factura' && f.pagada && !f.abonadaPor)
      if (this.filtro === 'Abonadas') lista = lista.filter(f => f.tipo === 'factura' && f.abonadaPor)
      if (this.filtro === 'Abonos') lista = lista.filter(f => f.tipo === 'abono')

      const q = this.buscar.toLowerCase().trim()
      if (!q) return lista
      return lista.filter(f => {
        const campos = [f.numero, this.nombreCliente(f), f.clienteEmail, f.pedidoRef, `#${f.pedidoId}`]
        return campos.some(c => c && c.toString().toLowerCase().includes(q))
      })
    },
  },
  methods: {
    nombreCliente(f) {
      return f.clienteEmpresa || [f.clienteNombre, f.clienteApellidos].filter(Boolean).join(' ') || '—'
    },
    formatFecha(f) {
      if (!f) return '--'
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleDateString('es-ES')
    },
    estadoInfo(f) {
      if (f.tipo === 'abono') return { texto: 'ABONO', clase: 'abono' }
      if (f.abonadaPor) return { texto: 'ABONADA', clase: 'abonada' }
      if (f.pagada) return { texto: 'PAGADA', clase: 'paid' }
      if (f.vencimiento && new Date(f.vencimiento) < new Date()) return { texto: 'VENCIDA', clase: 'over' }
      return { texto: 'PENDIENTE', clase: 'pend' }
    },
    sumaFiltradas() {
      const porMoneda = {}
      this.filtradas.forEach(f => {
        const s = f.simbolo || '£'
        porMoneda[s] = (porMoneda[s] || 0) + (Number(f.total) || 0)
      })
      return Object.keys(porMoneda)
        .map(s => `${s}${Math.round(porMoneda[s] * 100) / 100}`)
        .join(' + ')
    },
    verPdf(f) {
      this.pdfId = f.id
      axios
        .get(`/escaner/facturas/${f.id}/pdf`, { responseType: 'blob' })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => { this.notificar('No se pudo abrir el PDF', 'danger') })
        .finally(() => { this.pdfId = null })
    },
    abrirAbono(f) {
      this.abono = {
        visible: true, factura: f, motivo: '', emitiendo: false,
      }
    },
    emitirAbono() {
      this.abono.emitiendo = true
      axios
        .post(`/escaner/facturas/${this.abono.factura.id}/abono`, { motivo: this.abono.motivo.trim() })
        .then(({ data }) => {
          this.notificar(`Abono ${data.numero} emitido`)
          this.abono.visible = false
          this.load()
        })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo emitir el abono'
          this.notificar(msg, 'danger')
        })
        .finally(() => { this.abono.emitiendo = false })
    },
    load() {
      this.cargando = true
      axios
        .get('/escaner/facturas/todas')
        .then(({ data }) => { this.facturas = data || [] })
        .catch(() => { this.notificar('No se pudieron cargar las facturas', 'danger') })
        .finally(() => { this.cargando = false })
    },
    notificar(msg, variant = 'success') {
      this.$bvToast.toast(msg, { title: 'Facturas', variant, solidColor: true })
    },
  },
  mounted() {
    axios.post('/uso', { seccion: 'facturas' }).catch(() => {})
    this.load()
  },
}
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$success: #10B981;
$danger: #EF4444;
$warning: #F59E0B;
$text: #1E293B;
$text-muted: #64748B;
$border: #E2E8F0;

.fac-page { padding-bottom: 24px; }

.fac-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.fac-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
}

.fac-title {
  margin: 0;
  font-weight: 700;
  color: $text;
}

.fac-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 8px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid $border;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: $text;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border-top: 1px solid $border;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: $text-muted;
  }
}

.fac-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.pill {
  border: 1px solid $border;
  background: #fff;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: $text-muted;
  cursor: pointer;

  &.active {
    background: $primary;
    border-color: $primary;
    color: #fff;
  }
}

.search-box {
  position: relative;

  &__icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: $text-muted;
  }

  &__input {
    border: 1px solid $border;
    border-radius: 6px;
    padding: 6px 10px 6px 30px;
    font-size: 13px;
    min-width: 240px;

    &:focus { outline: none; border-color: $primary; }
  }
}

.table-wrapper { overflow-x: auto; }

.cell-mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
}

.cell-numero-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cell-numero {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 700;
}

.cell-total { font-weight: 700; }

.cell-cliente--link {
  color: $primary;
  cursor: pointer;
  font-weight: 600;

  &:hover { text-decoration: underline; }
}

.badge-doc {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 700;

  &--abono {
    background: rgba($warning, 0.12);
    color: darken($warning, 8%);
  }
}

.badge-status {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  border: 1px solid transparent;
  white-space: nowrap;

  &--paid { background: rgba($success, 0.08); color: $success; border-color: rgba($success, 0.35); }
  &--pend { background: rgba(#6e6b7b, 0.08); color: #6e6b7b; border-color: rgba(#6e6b7b, 0.3); }
  &--over { background: rgba($danger, 0.08); color: $danger; border-color: rgba($danger, 0.4); }
  &--abono { background: rgba($warning, 0.1); color: darken($warning, 8%); border-color: rgba($warning, 0.45); }
  &--abonada { background: rgba($text-muted, 0.1); color: $text-muted; border-color: rgba($text-muted, 0.3); }
}
</style>

<style lang="scss">
// Modo oscuro (sin scoped: .dark-layout está en body)
.dark-layout .fac-page {
  .fac-eyebrow { color: #8e9099; }
  .fac-title,
  .fac-card__title { color: #e4e6eb; }

  .fac-card { background: #1f1f1f; border-color: #2c2c2c; }
  .fac-card__header { border-bottom-color: #2c2c2c; }
  .fac-card__footer { border-top-color: #2c2c2c; color: #8e9099; b { color: #e4e6eb; } }

  .pill:not(.active) { background: #161616; border-color: #2c2c2c; color: #b4b7bd; }

  .search-box__icon { color: #8e9099; }
  .search-box__input {
    background: #161616;
    border-color: #2c2c2c;
    color: #d0d2d6;
    &::placeholder { color: #6e7079; }
    &:focus { border-color: #3B82F6; }
  }

  // Tabla al tono de la tarjeta (los estilos globales de Vuexy usan !important)
  .table { background: #1f1f1f !important; }
  .table thead th { background: #1f1f1f !important; color: #8e9099 !important; border-bottom-color: #2c2c2c !important; }
  .table tbody tr { background: #1f1f1f !important; }
  .table tbody tr td { border-top-color: #2c2c2c !important; color: #d0d2d6; }
  .table tbody tr:hover { background: #242424 !important; }
  .cell-numero, .cell-total { color: #e4e6eb; }
  .cell-total.text-danger { color: #EF4444; }
}
</style>
